/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PieChart from "examples/Charts/PieChart";

// Utilidades de exportación
import {
  exportDashboardToPDF,
  exportMultipleSheetsToExcel,
  handlePrint as printPage,
} from "utils/exportHelpers";

export default function ReportesFinancieros() {
  const [tipoReporte, setTipoReporte] = useState("balance");
  const [periodo, setPeriodo] = useState("mes");
  const [empresa, setEmpresa] = useState("todas");

  // Estados para datos de API
  const [balanceGeneral, setBalanceGeneral] = useState({
    activos: {
      corrientes: { items: [], total: 0 },
      noCorrientes: { items: [], total: 0 },
    },
    pasivos: {
      corrientes: { items: [], total: 0 },
      noCorrientes: { items: [], total: 0 },
    },
    patrimonio: { items: [], total: 0 },
  });
  const [estadoResultados, setEstadoResultados] = useState({
    ingresos: { items: [], total: 0 },
    costos: { items: [], total: 0 },
    gastos: { items: [], total: 0 },
    utilidadBruta: 0,
    utilidadNeta: 0,
  });
  const [flujoCaja, setFlujoCaja] = useState({ actividades: [] });
  const [ratiosFinancieros, setRatiosFinancieros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Obtener datos del API
  useEffect(() => {
    const fetchDatos = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(API_ENDPOINTS.REPORTES.FINANCIEROS, {
          params: { tipo: tipoReporte, periodo, empresa_id: empresa !== "todas" ? empresa : null },
        });

        const data = response.data;

        const formatCurrency = (value) => {
          const formatted = new Intl.NumberFormat("es-MX", {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Math.abs(value));
          return `$${formatted}`;
        };

        if (tipoReporte === "balance" && data.balance_general) {
          const bg = data.balance_general;
          setBalanceGeneral({
            activos: {
              corrientes: {
                total: bg.activos.corrientes.total,
                items: [
                  { cuenta: "Efectivo y Equivalentes", monto: bg.activos.corrientes.efectivo },
                  { cuenta: "Cuentas por Cobrar", monto: bg.activos.corrientes.cuentas_por_cobrar },
                  { cuenta: "Inventarios", monto: bg.activos.corrientes.inventarios },
                  {
                    cuenta: "Gastos Pagados por Anticipado",
                    monto: bg.activos.corrientes.gastos_pagados,
                  },
                ],
              },
              noCorrientes: {
                total: bg.activos.no_corrientes.total,
                items: [
                  {
                    cuenta: "Propiedad, Planta y Equipo",
                    monto: bg.activos.no_corrientes.propiedad_planta,
                  },
                  {
                    cuenta: "Depreciación Acumulada",
                    monto: bg.activos.no_corrientes.depreciacion,
                  },
                  {
                    cuenta: "Inversiones a Largo Plazo",
                    monto: bg.activos.no_corrientes.inversiones,
                  },
                  { cuenta: "Intangibles", monto: bg.activos.no_corrientes.intangibles },
                ],
              },
            },
            pasivos: {
              corrientes: {
                total: bg.pasivos.corrientes.total,
                items: [
                  { cuenta: "Cuentas por Pagar", monto: bg.pasivos.corrientes.cuentas_por_pagar },
                  {
                    cuenta: "Préstamos a Corto Plazo",
                    monto: bg.pasivos.corrientes.prestamos_corto,
                  },
                  { cuenta: "Impuestos por Pagar", monto: bg.pasivos.corrientes.impuestos },
                  {
                    cuenta: "Otras Obligaciones Corrientes",
                    monto: bg.pasivos.corrientes.otras_obligaciones,
                  },
                ],
              },
              noCorrientes: {
                total: bg.pasivos.no_corrientes.total,
                items: [
                  {
                    cuenta: "Préstamos a Largo Plazo",
                    monto: bg.pasivos.no_corrientes.prestamos_largo,
                  },
                  {
                    cuenta: "Obligaciones Laborales",
                    monto: bg.pasivos.no_corrientes.obligaciones_laborales,
                  },
                  { cuenta: "Otros Pasivos No Corrientes", monto: bg.pasivos.no_corrientes.otros },
                ],
              },
            },
            patrimonio: {
              total: bg.patrimonio.total,
              items: [
                { cuenta: "Capital Social", monto: bg.patrimonio.capital_social },
                { cuenta: "Reservas", monto: bg.patrimonio.reservas },
                { cuenta: "Utilidades Retenidas", monto: bg.patrimonio.utilidades_retenidas },
                { cuenta: "Utilidad del Ejercicio", monto: bg.patrimonio.utilidad_ejercicio },
              ],
            },
          });
        } else if (tipoReporte === "resultados" && data.estado_resultados) {
          const er = data.estado_resultados;
          setEstadoResultados({
            ingresos: {
              total: er.ingresos_totales,
              items: [
                { concepto: "Ventas de Productos", monto: er.ingresos.ventas_productos },
                { concepto: "Prestación de Servicios", monto: er.ingresos.servicios },
                { concepto: "Alquileres", monto: er.ingresos.alquileres },
                { concepto: "Otros Ingresos", monto: er.ingresos.otros },
              ],
            },
            costos: {
              total: er.costos_totales,
              items: [
                { concepto: "Costo de Ventas", monto: er.costos.costo_ventas },
                { concepto: "Costo de Servicios", monto: er.costos.costo_servicios },
              ],
            },
            gastos: {
              total: er.gastos_totales,
              items: [
                { concepto: "Gastos de Administración", monto: er.gastos.administracion },
                { concepto: "Gastos de Ventas", monto: er.gastos.ventas },
                { concepto: "Gastos Financieros", monto: er.gastos.financieros },
                { concepto: "Otros Gastos", monto: er.gastos.otros },
              ],
            },
            utilidadBruta: er.utilidad_bruta,
            utilidadNeta: er.utilidad_neta,
          });
        } else if (tipoReporte === "flujo" && data.flujo_caja) {
          const fc = data.flujo_caja;
          setFlujoCaja({
            actividades: [
              {
                tipo: "Operación",
                total: fc.operacion.total,
                items: [
                  { concepto: "Cobros de Clientes", monto: fc.operacion.cobros_clientes },
                  { concepto: "Pagos a Proveedores", monto: fc.operacion.pagos_proveedores },
                  { concepto: "Pagos a Empleados", monto: fc.operacion.pagos_empleados },
                  { concepto: "Otros Pagos Operativos", monto: fc.operacion.otros_pagos },
                ],
              },
              {
                tipo: "Inversión",
                total: fc.inversion.total,
                items: [
                  { concepto: "Compra de Activos Fijos", monto: fc.inversion.compra_activos },
                  { concepto: "Venta de Inversiones", monto: fc.inversion.venta_inversiones },
                  { concepto: "Dividendos Recibidos", monto: fc.inversion.dividendos_recibidos },
                ],
              },
              {
                tipo: "Financiamiento",
                total: fc.financiamiento.total,
                items: [
                  { concepto: "Nuevos Préstamos", monto: fc.financiamiento.nuevos_prestamos },
                  { concepto: "Pago de Préstamos", monto: fc.financiamiento.pago_prestamos },
                  { concepto: "Dividendos Pagados", monto: fc.financiamiento.dividendos_pagados },
                ],
              },
            ],
            flujoNeto: fc.flujo_neto,
            saldoInicial: fc.saldo_inicial,
            saldoFinal: fc.saldo_final,
          });
        } else if (tipoReporte === "ratios" && data.ratios_financieros) {
          const rf = data.ratios_financieros;
          setRatiosFinancieros([
            {
              categoria: "Liquidez",
              ratio: "Razón Corriente",
              valor: rf.liquidez.corriente.toFixed(2),
              formula: "Activo Corriente / Pasivo Corriente",
            },
            {
              categoria: "Liquidez",
              ratio: "Razón Rápida",
              valor: rf.liquidez.rapida.toFixed(2),
              formula: "(Act. Corriente - Inventarios) / Pas. Corriente",
            },
            {
              categoria: "Liquidez",
              ratio: "Razón de Efectivo",
              valor: rf.liquidez.efectivo.toFixed(2),
              formula: "Efectivo / Pasivo Corriente",
            },
            {
              categoria: "Rentabilidad",
              ratio: "ROE (%)",
              valor: rf.rentabilidad.roe.toFixed(1) + "%",
              formula: "Utilidad Neta / Patrimonio × 100",
            },
            {
              categoria: "Rentabilidad",
              ratio: "ROA (%)",
              valor: rf.rentabilidad.roa.toFixed(1) + "%",
              formula: "Utilidad Neta / Activos × 100",
            },
            {
              categoria: "Rentabilidad",
              ratio: "Margen Neto (%)",
              valor: rf.rentabilidad.margen_neto.toFixed(1) + "%",
              formula: "Utilidad Neta / Ventas × 100",
            },
            {
              categoria: "Rentabilidad",
              ratio: "Margen Bruto (%)",
              valor: rf.rentabilidad.margen_bruto.toFixed(1) + "%",
              formula: "(Ventas - Costo) / Ventas × 100",
            },
            {
              categoria: "Endeudamiento",
              ratio: "Endeudamiento Total (%)",
              valor: rf.endeudamiento.total.toFixed(1) + "%",
              formula: "Pasivo Total / Activo Total × 100",
            },
            {
              categoria: "Endeudamiento",
              ratio: "Deuda a Patrimonio (%)",
              valor: rf.endeudamiento.patrimonio.toFixed(1) + "%",
              formula: "Pasivo / Patrimonio × 100",
            },
            {
              categoria: "Endeudamiento",
              ratio: "Cobertura de Intereses",
              valor: rf.endeudamiento.cobertura_intereses.toFixed(1) + "x",
              formula: "EBIT / Gastos Financieros",
            },
            {
              categoria: "Actividad",
              ratio: "Rotación de Activos",
              valor: rf.actividad.rotacion_activos.toFixed(2) + "x",
              formula: "Ventas / Activos Totales",
            },
            {
              categoria: "Actividad",
              ratio: "Rotación de Inventario",
              valor: rf.actividad.rotacion_inventario.toFixed(1) + "x",
              formula: "Costo Ventas / Inventario Promedio",
            },
            {
              categoria: "Actividad",
              ratio: "Días de Inventario",
              valor: Math.round(rf.actividad.dias_inventario) + " días",
              formula: "365 / Rotación de Inventario",
            },
          ]);
        }
      } catch (error) {
        console.error("Error al cargar reportes financieros:", error);
        setError("Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [tipoReporte, periodo, empresa]);

  const handleExportPDF = async () => {
    await exportDashboardToPDF(
      "reportes-financieros-content",
      `reporte-financiero-${tipoReporte}-${new Date().getTime()}.pdf`
    );
  };

  const handleExportExcel = () => {
    const sheets = [];

    // Balance General
    if (tipoReporte === "balance" || tipoReporte === "todos") {
      const balanceData = [
        // Activos Corrientes
        { Categoría: "ACTIVOS CORRIENTES", Cuenta: "", Monto: "" },
        ...balanceGeneral.activos.corrientes.items.map((item) => ({
          Categoría: "",
          Cuenta: item.cuenta,
          Monto: item.monto,
        })),
        {
          Categoría: "",
          Cuenta: "Total Activos Corrientes",
          Monto: balanceGeneral.activos.corrientes.total,
        },
        { Categoría: "", Cuenta: "", Monto: "" },
        // Activos No Corrientes
        { Categoría: "ACTIVOS NO CORRIENTES", Cuenta: "", Monto: "" },
        ...balanceGeneral.activos.noCorrientes.items.map((item) => ({
          Categoría: "",
          Cuenta: item.cuenta,
          Monto: item.monto,
        })),
        {
          Categoría: "",
          Cuenta: "Total Activos No Corrientes",
          Monto: balanceGeneral.activos.noCorrientes.total,
        },
        { Categoría: "", Cuenta: "", Monto: "" },
        // Pasivos Corrientes
        { Categoría: "PASIVOS CORRIENTES", Cuenta: "", Monto: "" },
        ...balanceGeneral.pasivos.corrientes.items.map((item) => ({
          Categoría: "",
          Cuenta: item.cuenta,
          Monto: item.monto,
        })),
        {
          Categoría: "",
          Cuenta: "Total Pasivos Corrientes",
          Monto: balanceGeneral.pasivos.corrientes.total,
        },
        { Categoría: "", Cuenta: "", Monto: "" },
        // Pasivos No Corrientes
        { Categoría: "PASIVOS NO CORRIENTES", Cuenta: "", Monto: "" },
        ...balanceGeneral.pasivos.noCorrientes.items.map((item) => ({
          Categoría: "",
          Cuenta: item.cuenta,
          Monto: item.monto,
        })),
        {
          Categoría: "",
          Cuenta: "Total Pasivos No Corrientes",
          Monto: balanceGeneral.pasivos.noCorrientes.total,
        },
        { Categoría: "", Cuenta: "", Monto: "" },
        // Patrimonio
        { Categoría: "PATRIMONIO", Cuenta: "", Monto: "" },
        ...balanceGeneral.patrimonio.items.map((item) => ({
          Categoría: "",
          Cuenta: item.cuenta,
          Monto: item.monto,
        })),
        { Categoría: "", Cuenta: "Total Patrimonio", Monto: balanceGeneral.patrimonio.total },
      ];
      sheets.push({ name: "Balance General", data: balanceData });
    }

    // Estado de Resultados
    if (tipoReporte === "resultados" || tipoReporte === "todos") {
      const resultadosData = [
        { Categoría: "INGRESOS", Concepto: "", Monto: "" },
        ...estadoResultados.ingresos.items.map((item) => ({
          Categoría: "",
          Concepto: item.concepto,
          Monto: item.monto,
        })),
        { Categoría: "", Concepto: "Total Ingresos", Monto: estadoResultados.ingresos.total },
        { Categoría: "", Concepto: "", Monto: "" },
        { Categoría: "COSTOS", Concepto: "", Monto: "" },
        ...estadoResultados.costos.items.map((item) => ({
          Categoría: "",
          Concepto: item.concepto,
          Monto: item.monto,
        })),
        { Categoría: "", Concepto: "Total Costos", Monto: estadoResultados.costos.total },
        { Categoría: "", Concepto: "", Monto: "" },
        { Categoría: "GASTOS", Concepto: "", Monto: "" },
        ...estadoResultados.gastos.items.map((item) => ({
          Categoría: "",
          Concepto: item.concepto,
          Monto: item.monto,
        })),
        { Categoría: "", Concepto: "Total Gastos", Monto: estadoResultados.gastos.total },
        { Categoría: "", Concepto: "", Monto: "" },
        { Categoría: "", Concepto: "UTILIDAD NETA", Monto: estadoResultados.utilidadNeta },
      ];
      sheets.push({ name: "Estado de Resultados", data: resultadosData });
    }

    // Ratios Financieros
    if (tipoReporte === "ratios" || tipoReporte === "todos") {
      const ratiosData = ratiosFinancieros.flatMap((categoria) => [
        { Categoría: categoria.nombre, Indicador: "", Valor: "", Estado: "" },
        ...categoria.indicadores.map((indicador) => ({
          Categoría: "",
          Indicador: indicador.nombre,
          Valor: indicador.valor,
          Estado: indicador.estado,
        })),
        { Categoría: "", Indicador: "", Valor: "", Estado: "" },
      ]);
      sheets.push({ name: "Ratios Financieros", data: ratiosData });
    }

    if (sheets.length === 0) {
      sheets.push({ name: tipoReporte, data: [{ Info: "No hay datos disponibles" }] });
    }

    exportMultipleSheetsToExcel(
      sheets,
      `reporte-financiero-${tipoReporte}-${new Date().getTime()}.xlsx`
    );
  };

  const handlePrintReport = () => {
    printPage();
  };

  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat("es-MX", {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
    return `$${formatted}`;
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "alto":
        return "success";
      case "normal":
        return "info";
      case "bajo":
        return "warning";
      default:
        return "dark";
    }
  };

  const renderBalance = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              ACTIVOS
            </MDTypography>

            <MDTypography variant="button" fontWeight="bold" color="dark" mb={1}>
              Activos Corrientes
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {balanceGeneral.activos.corrientes.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Subtotal Activos Corrientes</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(balanceGeneral.activos.corrientes.total)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} mt={3}>
              Activos No Corrientes
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {balanceGeneral.activos.noCorrientes.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Subtotal Activos No Corrientes</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(balanceGeneral.activos.noCorrientes.total)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="bold" color="success">
                TOTAL ACTIVOS
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" color="success">
                {formatCurrency(
                  balanceGeneral.activos.corrientes.total +
                  balanceGeneral.activos.noCorrientes.total
                )}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              PASIVOS Y PATRIMONIO
            </MDTypography>

            <MDTypography variant="button" fontWeight="bold" color="dark" mb={1}>
              Pasivos Corrientes
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {balanceGeneral.pasivos.corrientes.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Subtotal Pasivos Corrientes</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(balanceGeneral.pasivos.corrientes.total)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} mt={3}>
              Pasivos No Corrientes
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {balanceGeneral.pasivos.noCorrientes.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Subtotal Pasivos No Corrientes</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(balanceGeneral.pasivos.noCorrientes.total)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} mt={3}>
              Patrimonio
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {balanceGeneral.patrimonio.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>Total Patrimonio</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(balanceGeneral.patrimonio.total)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="bold" color="error">
                TOTAL PASIVOS + PATRIMONIO
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" color="error">
                {formatCurrency(
                  balanceGeneral.pasivos.corrientes.total +
                  balanceGeneral.pasivos.noCorrientes.total +
                  balanceGeneral.patrimonio.total
                )}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Composición del Balance General
            </MDTypography>
            <PieChart chart={{ labels: ["Activos Corrientes", "Activos No Corrientes"], datasets: [{ label: "Composición", backgroundColors: ["info", "dark"], data: [balanceGeneral.activos.corrientes.total, balanceGeneral.activos.noCorrientes.total] }] }} height="300px" />
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderEstadoResultados = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              ESTADO DE RESULTADOS
            </MDTypography>

            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <MDTypography variant="button" fontWeight="bold" color="success">
                        INGRESOS
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  {estadoResultados.ingresos.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.concepto}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell>
                      <strong>Total Ingresos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(estadoResultados.ingresos.total)}</strong>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2} sx={{ paddingTop: 2 }}>
                      <MDTypography variant="button" fontWeight="bold" color="error">
                        COSTOS
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  {estadoResultados.costos.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.concepto}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell>
                      <strong>Total Costos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(estadoResultados.costos.total)}</strong>
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                    <TableCell>
                      <strong>Utilidad Bruta</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {formatCurrency(
                          estadoResultados.ingresos.total - estadoResultados.costos.total
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2} sx={{ paddingTop: 2 }}>
                      <MDTypography variant="button" fontWeight="bold" color="warning">
                        GASTOS OPERATIVOS
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  {estadoResultados.gastos.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.concepto}</TableCell>
                      <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell>
                      <strong>Total Gastos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(estadoResultados.gastos.total)}</strong>
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: "#c8e6c9" }}>
                    <TableCell>
                      <MDTypography variant="h6" fontWeight="bold">
                        UTILIDAD NETA
                      </MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="h6" fontWeight="bold" color="success">
                        {formatCurrency(estadoResultados.utilidadNeta)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Márgenes
            </MDTypography>
            <MDBox mb={2}>
              <MDTypography variant="button" color="text">
                Margen Bruto
              </MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="success">
                58.0%
              </MDTypography>
            </MDBox>
            <Divider />
            <MDBox my={2}>
              <MDTypography variant="button" color="text">
                Margen Operativo
              </MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="info">
                27.9%
              </MDTypography>
            </MDBox>
            <Divider />
            <MDBox mt={2}>
              <MDTypography variant="button" color="text">
                Margen Neto
              </MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="warning">
                27.5%
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Distribución
            </MDTypography>
            <MDBox mb={1.5}>
              <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="caption">Costos</MDTypography>
                <MDTypography variant="caption" fontWeight="bold">
                  42.0%
                </MDTypography>
              </MDBox>
              <MDBox sx={{ backgroundColor: "#e0e0e0", borderRadius: 1, height: 8 }}>
                <MDBox
                  sx={{ backgroundColor: "#f44336", borderRadius: 1, height: 8, width: "42%" }}
                />
              </MDBox>
            </MDBox>
            <MDBox mb={1.5}>
              <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="caption">Gastos</MDTypography>
                <MDTypography variant="caption" fontWeight="bold">
                  30.5%
                </MDTypography>
              </MDBox>
              <MDBox sx={{ backgroundColor: "#e0e0e0", borderRadius: 1, height: 8 }}>
                <MDBox
                  sx={{ backgroundColor: "#ff9800", borderRadius: 1, height: 8, width: "30.5%" }}
                />
              </MDBox>
            </MDBox>
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="caption">Utilidad</MDTypography>
                <MDTypography variant="caption" fontWeight="bold">
                  27.5%
                </MDTypography>
              </MDBox>
              <MDBox sx={{ backgroundColor: "#e0e0e0", borderRadius: 1, height: 8 }}>
                <MDBox
                  sx={{ backgroundColor: "#4caf50", borderRadius: 1, height: 8, width: "27.5%" }}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Evolución de Ingresos por Tipo
            </MDTypography>
            <DefaultLineChart chart={{ labels: [], datasets: [{ label: "Ingresos", data: [] }] }} height="300px" />
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFlujoCaja = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              ESTADO DE FLUJO DE EFECTIVO
            </MDTypography>

            <TableContainer>
              <Table>
                <TableBody>
                  {flujoCaja.actividades.map((actividad, actIndex) => (
                    <>
                      <TableRow key={actIndex}>
                        <TableCell colSpan={2}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color={actIndex === 0 ? "success" : actIndex === 1 ? "info" : "warning"}
                          >
                            Actividades de {actividad.tipo}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      {actividad.items.map((item, itemIndex) => (
                        <TableRow key={itemIndex}>
                          <TableCell sx={{ pl: 4 }}>{item.concepto}</TableCell>
                          <TableCell align="right">{formatCurrency(item.monto)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>
                          <strong>Flujo neto de {actividad.tipo}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(actividad.total)}</strong>
                        </TableCell>
                      </TableRow>
                    </>
                  ))}

                  <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="bold">
                        Aumento Neto en Efectivo
                      </MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="button" fontWeight="bold">
                        {formatCurrency(flujoCaja.aumentoNeto)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>Efectivo al Inicio del Período</TableCell>
                    <TableCell align="right">{formatCurrency(flujoCaja.efectivoInicial)}</TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: "#c8e6c9" }}>
                    <TableCell>
                      <MDTypography variant="h6" fontWeight="bold">
                        Efectivo al Final del Período
                      </MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="h6" fontWeight="bold" color="success">
                        {formatCurrency(flujoCaja.efectivoFinal)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRatios = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Evolución de Ratios de Rentabilidad
            </MDTypography>
            <DefaultLineChart chart={{ labels: [], datasets: [{ label: "Ratios", data: [] }] }} height="300px" />
          </MDBox>
        </Card>
      </Grid>

      {ratiosFinancieros.map((categoria, catIndex) => (
        <Grid item xs={12} md={6} key={catIndex}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Ratios de {categoria.categoria}
              </MDTypography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Ratio</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Valor</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Estado</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoria.ratios.map((ratio, ratioIndex) => (
                      <TableRow key={ratioIndex}>
                        <TableCell>
                          <MDTypography variant="button" fontWeight="medium">
                            {ratio.nombre}
                          </MDTypography>
                          <MDTypography variant="caption" display="block" color="text">
                            {ratio.formula}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="button" fontWeight="bold">
                            {ratio.valor}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDBox
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            display="inline-block"
                            sx={{
                              backgroundColor: ({ palette }) =>
                                palette[getEstadoColor(ratio.estado)].main,
                              color: "white",
                            }}
                          >
                            <MDTypography variant="caption" fontWeight="medium" color="white">
                              {ratio.estado.toUpperCase()}
                            </MDTypography>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderContent = () => {
    switch (tipoReporte) {
      case "balance":
        return renderBalance();
      case "resultados":
        return renderEstadoResultados();
      case "flujo":
        return renderFlujoCaja();
      case "ratios":
        return renderRatios();
      default:
        return renderBalance();
    }
  };

  return (
    <MDBox id="reportes-financieros-content">
      {/* Filtros y Controles */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Tipo de Reporte"
                value={tipoReporte}
                onChange={(e) => setTipoReporte(e.target.value)}
                fullWidth
              >
                <MenuItem value="balance">Balance General</MenuItem>
                <MenuItem value="resultados">Estado de Resultados</MenuItem>
                <MenuItem value="flujo">Flujo de Efectivo</MenuItem>
                <MenuItem value="ratios">Ratios Financieros</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Período"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                fullWidth
              >
                <MenuItem value="mes">Este Mes</MenuItem>
                <MenuItem value="trimestre">Este Trimestre</MenuItem>
                <MenuItem value="semestre">Este Semestre</MenuItem>
                <MenuItem value="anio">Este Año</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Empresa"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                fullWidth
              >
                <MenuItem value="todas">Todas las Empresas</MenuItem>
                <MenuItem value="empresa1">Empresa Principal S.A.</MenuItem>
                <MenuItem value="empresa2">Sucursal Comercial Ltda.</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="dark" size="small" onClick={handleExportPDF}>
                  <Icon>picture_as_pdf</Icon>&nbsp;PDF
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleExportExcel}
                >
                  <Icon>table_chart</Icon>&nbsp;Excel
                </MDButton>
                <MDButton variant="outlined" color="info" size="small" onClick={handlePrintReport}>
                  <Icon>print</Icon>
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Contenido del Reporte */}
      {loading ? (
        <MDBox display="flex" justifyContent="center" p={6}>
          <CircularProgress color="info" />
        </MDBox>
      ) : (
        renderContent()
      )}
    </MDBox>
  );
}
